<template>
  <div>
    <b-container v-if="summaryAvailable">
      <div v-if="canEditPoster" class="poster-edit-bar">
        <button type="button" class="poster-edit-button" @click="$refs.editor.open()">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="currentColor" d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zm17.7-10.2a1 1 0 0 0 0-1.42l-2.33-2.33a1 1 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
          {{ t('posterEdit') }}
        </button>
      </div>
      <PosterTimeline
        :title="ride.title"
        :attractionsByDay="days"
        :settings="posterSettings"
        @open-stop="scrollToRideDay($event.dayIndex)"
      />
      <PosterEditor v-if="canEditPoster" ref="editor" :ride="ride" :days="days"/>
    </b-container>
    <RideCopyOffer :ride="ride"/>
    <b-container class="pt-5">
      <div class="single_ride_sumary">
        <main>
          <section>
            <PosterHostCard/>
            <RideDates :ride="ride"/>
            <RideMedia :ride="ride"/>
            <div class="trip-date-section">
              <div class="container">
                <PosterStory :ride="ride"/>
                <ClassicTimeline v-if="summaryAvailable"/>
                <PosterGeneralInfo :ride="ride"/>
              </div>
            </div>
          </section>
        </main>
      </div>
    </b-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import RideCopyOffer from "@/components/RideSummary/RideCopyOffer";
import RideDates from "@/components/RideSummary/RideDates";
import RideMedia from "@/components/RideSummary/RideMedia";
import ClassicTimeline from "@/designs/classic/ClassicTimeline";
import { scrollToRideDay } from "@/utils/scrollToRideElement";
import PosterTimeline from "./PosterTimeline";
import PosterHostCard from "./PosterHostCard";
import PosterStory from "./PosterStory";
import PosterGeneralInfo from "./PosterGeneralInfo";
import PosterEditor from "./PosterEditor";
import { designSettings } from "@/utils/rideDesigns";
import { POSTER_SETTINGS_VERSION } from "@/utils/posterStops";

/**
 * The poster trip page: the zig-zag timeline of stops first, before anything
 * else, then a compact ride manager card, the dates and the description cut
 * to a few lines, then the same summary and map as the classic design
 * (ClassicTimeline): beside each other on desktop, a list/map swap on mobile.
 * Its activities are one collapsible row per day, and it opens on them, as
 * the poster already shows the stays. Last, the general information as
 * collapsible rows. The ride's manager gets an edit button over the poster
 * (PosterEditor): design, the days of each stop, and each stop's photo. The
 * page header is compact (compactHeader in
 * src/designs/index.js), as the poster carries the title.
 *
 * A design layout -- see src/designs/index.js for the contract every layout
 * keeps (props, and the .single_ride_sumary element).
 */
export default {
  name: 'PosterLayout',
  mixins: [translationMixin],
  components: {
    RideCopyOffer,
    RideDates,
    RideMedia,
    ClassicTimeline,
    PosterTimeline,
    PosterHostCard,
    PosterStory,
    PosterGeneralInfo,
    PosterEditor,
  },
  props: {
    ride: { type: Object, required: true },
    days: { type: Array, default: () => [] },
    summaryAvailable: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters(['compareToUserId', 'isUserManager', 'isRideLocked']),
    // The manager's splits and photos, or null for an automatic poster.
    posterSettings() {
      return designSettings(this.ride, 'poster', POSTER_SETTINGS_VERSION);
    },
    // Who may edit the poster: the same people who may pick the design in the
    // ride settings (a manager, on their own ride), never on a locked ride.
    // The server checks ownership again on save.
    canEditPoster() {
      return this.isUserManager && this.compareToUserId(this.ride.authorId) && !this.isRideLocked;
    },
  },
  methods: {
    scrollToRideDay,
  },
};
</script>

<style scoped>
.poster-edit-bar {
  display: flex;
  justify-content: flex-end;
  max-width: 760px;
  margin: 12px auto -4px;
  /* The container has no side padding on a phone: keep off the screen edge. */
  padding: 0 12px;
}

.poster-edit-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 0;
  border-radius: 999px;
  background: #fff;
  box-shadow: inset 0 0 0 1.5px var(--primary-color, #07A0C3);
  color: var(--primary-color, #07A0C3);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
</style>
